const milestones = [
  {
    year: "1961",
    title: "Founded in San Rafael",
    desc: "Don Collins establishes indoor vehicle storage operations in San Rafael, CA — building a reputation for reliability across Marin County.",
  },
  {
    year: "1980s",
    title: "Point Richmond Expansion",
    desc: "A second fully-alarmed indoor facility opens on Canal Blvd in Point Richmond, extending service coverage to the East Bay.",
  },
  {
    year: "2020s",
    title: "New Ownership, Same Standards",
    desc: "Jim Long becomes the new owner and operator, committed to continuing the legacy of quality, secure indoor storage Bay Area customers have depended on for decades.",
  },
  {
    year: "Today",
    title: "Two Secure Locations",
    desc: "Two fully-alarmed indoor facilities in San Rafael and Point Richmond serve collectors, enthusiasts, and everyday vehicle owners across the Bay Area.",
  },
];

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Security First",
    desc: "Both facilities are fully alarmed with controlled indoor access. Your vehicle is protected from theft, vandalism, and weather at all times.",
  },
  {
    icon: "ri-building-3-line",
    title: "Indoor-Only Storage",
    desc: "We believe every vehicle stored with us should be fully indoors. No outdoor lots. No partial cover. Complete indoor protection, always.",
  },
  {
    icon: "ri-handshake-line",
    title: "Personal Service",
    desc: "Storage is by appointment. We want to give every customer the attention they deserve — call first so we can be ready for your visit.",
  },
  {
    icon: "ri-time-line",
    title: "Long-Term Focus",
    desc: "We offer the best rates for long-term storage. Our facilities are designed for extended-term preservation, not quick turnovers.",
  },
];

export default function AboutContent() {
  return (
    <section id="about-content" className="bg-[#0F0F0F] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="font-sans text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">
            Our Story
          </p>
          <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold mb-4">
            A Legacy of Secure Indoor Storage
          </h2>
          <div className="w-16 h-0.5 bg-gold mx-auto"></div>
        </div>

        {/* Founder / Operator Split */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="font-sans text-gold text-xs tracking-[0.2em] uppercase font-semibold mb-4">
              The Founder
            </p>
            <h3 className="font-serif text-white text-3xl font-semibold mb-5 leading-tight">
              Don Collins — 65 Years in the Automotive Industry
            </h3>
            <p className="font-sans text-[#9A9A9A] text-base leading-relaxed mb-5">
              Original owner and operator Don Collins dedicated over 65 years to the automotive industry in San Rafael. A trusted name throughout Marin County, Don built his storage reputation on a straightforward promise: fully-alarmed, secure indoor facilities with no shortcuts.
            </p>
            <p className="font-sans text-[#9A9A9A] text-base leading-relaxed">
              Don is now retired, but the standard he set — secure, clean, professional indoor vehicle storage — remains at the core of how the business is run today.
            </p>
          </div>

          <div className="relative">
            <div className="bg-charcoal-200 rounded-lg overflow-hidden border border-white/5">
              <img
                src="https://public.readdy.ai/ai/img_res/cd2decb0948df37d3606bc8385e0e9f5.jpg"
                alt="Don Collins Auto Storage indoor facility"
                className="w-full h-64 object-cover object-top"
              />
              <div className="p-6 border-t border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gold/10 rounded-full">
                    <i className="ri-user-star-line text-gold text-lg"></i>
                  </div>
                  <div>
                    <p className="font-sans text-white font-medium text-sm">Jim Long</p>
                    <p className="font-sans text-[#9A9A9A] text-xs">Current Owner &amp; Operator</p>
                  </div>
                </div>
                <p className="font-sans text-[#9A9A9A] text-sm leading-relaxed mt-4">
                  Jim Long is your current owner and operator — continuing the same standards Bay Area customers have relied on for decades. &ldquo;I&apos;ll do my best to give you the good service you are accustomed to.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <p className="font-sans text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">Timeline</p>
            <h3 className="font-serif text-white text-3xl font-bold">Decades of Trusted Service</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((m) => (
              <div key={m.year} className="bg-charcoal-200 rounded-lg p-6 border border-white/5 hover:border-gold/25 transition-colors">
                <span className="font-serif text-gold text-3xl font-bold block mb-3">{m.year}</span>
                <h4 className="font-sans text-white font-semibold text-sm mb-2">{m.title}</h4>
                <p className="font-sans text-[#9A9A9A] text-xs leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div>
          <div className="text-center mb-12">
            <p className="font-sans text-gold text-xs tracking-[0.3em] uppercase font-semibold mb-4">Our Values</p>
            <h3 className="font-serif text-white text-3xl font-bold">What We Stand For</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-charcoal-200 rounded-lg p-6 border border-white/5 text-center hover:border-gold/25 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-4">
                  <i className={`${v.icon} text-gold text-xl`}></i>
                </div>
                <h4 className="font-serif text-white font-semibold text-lg mb-2">{v.title}</h4>
                <p className="font-sans text-[#9A9A9A] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
